import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Search,
  Plus,
  Minus,
  Trash2,
  Package,
  User,
  Calendar,
  Save,
  X,
} from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { updateLayaway } from '@/api';
import { formatInTimeZone } from 'date-fns-tz';

interface EditLayawayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  layaway: any;
  items: any[];
  inventory: any[];
  branches: any[];
  vatPercentage: number;
  currencySymbol: string;
  systemTimezone: string;
  onSuccess: () => void;
}

export const EditLayawayDialog: React.FC<EditLayawayDialogProps> = ({
  open,
  onOpenChange,
  layaway,
  items,
  inventory,
  branches,
  vatPercentage,
  currencySymbol,
  systemTimezone,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [cart, setCart] = useState<any[]>([]);
  const [customer, setCustomer] = useState({
    name: '',
    phone: '',
    address: '',
  });
  const [productHandling, setProductHandling] = useState<'hold' | 'handover'>('hold');
  const [paymentScheduleMonths, setPaymentScheduleMonths] = useState(1);
  const [notes, setNotes] = useState('');
  const [layawayDate, setLayawayDate] = useState('');

  // Initialize form data when layaway changes
  const formatDateInputValue = useCallback((value?: string) => {
    if (!value) return '';
    try {
      return formatInTimeZone(new Date(value), systemTimezone, 'yyyy-MM-dd');
    } catch {
      return '';
    }
  }, [systemTimezone]);

  useEffect(() => {
    if (layaway) {
      setCustomer({
        name: layaway.customer_name || '',
        phone: layaway.customer_phone || '',
        address: layaway.customer_address || '',
      });
      setProductHandling(layaway.product_handling || 'hold');
      setPaymentScheduleMonths(layaway.payment_schedule_months || 1);
      setNotes(layaway.notes || '');

      // Set layaway date
      if (layaway.layaway_date) {
        setLayawayDate(formatDateInputValue(layaway.layaway_date));
      } else if (layaway.created_at) {
        setLayawayDate(formatDateInputValue(layaway.created_at));
      } else {
        setLayawayDate(new Date().toISOString().split('T')[0]);
      }

      // Convert layaway items to cart format
      if (layaway.items) {
        setCart(layaway.items.map((item: any) => ({
          id: item.item_id || item.id,
          name: item.name,
          sku: item.sku,
          price: parseFloat(item.price),
          quantity: parseInt(item.quantity),
          vat_enabled: item.vat_enabled,
        })));
      }
    }
  }, [layaway, formatDateInputValue]);

  // Get branch inventory for the layaway's branch
  const branchId = layaway?.branch_id;

  // Calculate available quantities for items based on layaway's branch
  const itemsWithAvailability = useMemo(() => {
    return items.map(item => {
      // Get inventory records for this item in the layaway's branch
      const branchInventory = inventory.filter(inv =>
        inv.item_id === item.id &&
        inv.branch_id === branchId
      );

      // Calculate total available quantity from inventory
      const totalAvailable = branchInventory.reduce((sum, inv) => {
        const available = inv.available_quantity !== undefined ? inv.available_quantity : inv.quantity;
        return sum + parseInt(available || 0, 10);
      }, 0);

      // Get the original quantity that was in this layaway (from the initial layaway data)
      const originalLayawayItem = layaway?.items?.find(li => li.item_id === item.id || li.id === item.id);
      const originalQuantity = originalLayawayItem ? originalLayawayItem.quantity : 0;

      // Get current quantity in cart
      const currentCartItem = cart.find(c => c.id === item.id);
      const currentCartQuantity = currentCartItem ? currentCartItem.quantity : 0;

      // Available = total inventory + what was originally reserved - what's currently in cart
      const adjustedAvailable = totalAvailable + originalQuantity - currentCartQuantity;

      return {
        ...item,
        availableQuantity: Math.max(0, adjustedAvailable),
        originalQuantity: item.quantity
      };
    });
  }, [items, inventory, branchId, cart, layaway?.items]);

  // Filter items for search
  const filteredItems = useMemo(() => {
    let filtered = itemsWithAvailability;

    if (itemSearchTerm) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
        item.sku.toLowerCase().includes(itemSearchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [itemsWithAvailability, itemSearchTerm]);

  // Calculate totals
  const cartSubtotal = useMemo(() => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  }, [cart]);

  const cartVAT = useMemo(() => {
    return cart.reduce((total, item) => {
      if (item.vat_enabled) {
        const itemTotal = item.price * item.quantity;
        return total + (itemTotal * (vatPercentage / 100));
      }
      return total;
    }, 0);
  }, [cart, vatPercentage]);

  const cartTotal = useMemo(() => {
    return cartSubtotal + cartVAT;
  }, [cartSubtotal, cartVAT]);

  const remainingAfterPayments = useMemo(() => {
    const paidAmount = layaway?.paid_amount || 0;
    return Math.max(0, cartTotal - paidAmount);
  }, [cartTotal, layaway?.paid_amount]);

  const addToCart = (item: any) => {
    // Calculate true available quantity for this specific item
    const branchInventory = inventory.filter(inv =>
      inv.item_id === item.id &&
      inv.branch_id === branchId
    );

    const totalInventoryAvailable = branchInventory.reduce((sum, inv) => {
      const available = inv.available_quantity !== undefined ? inv.available_quantity : inv.quantity;
      return sum + parseInt(available || 0, 10);
    }, 0);

    // Get original quantity from layaway
    const originalLayawayItem = layaway?.items?.find(li => li.item_id === item.id || li.id === item.id);
    const originalQuantity = originalLayawayItem ? originalLayawayItem.quantity : 0;

    // Get current cart quantity
    const existingItem = cart.find(cartItem => cartItem.id === item.id);
    const currentCartQty = existingItem ? existingItem.quantity : 0;

    // Calculate maximum we can have: inventory + what was originally reserved
    const maxAllowed = totalInventoryAvailable + originalQuantity;

    if (currentCartQty >= maxAllowed) {
      toast.error(`Cannot add more items. Maximum available: ${maxAllowed}`);
      return;
    }

    if (existingItem) {
      setCart(cart.map(cartItem =>
        cartItem.id === item.id
          ? { ...cartItem, quantity: cartItem.quantity + 1 }
          : cartItem
      ));
    } else {
      if (maxAllowed > 0) {
        setCart([...cart, {
          id: item.id,
          name: item.name,
          price: parseFloat(item.selling_price),
          quantity: 1,
          sku: item.sku,
          vat_enabled: item.vat_enabled,
        }]);
      } else {
        toast.error('Item is out of stock');
      }
    }
  };

  const updateCartQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      setCart(cart.filter(item => item.id !== itemId));
      return;
    }

    // Calculate true available quantity for this specific item
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    const branchInventory = inventory.filter(inv =>
      inv.item_id === itemId &&
      inv.branch_id === branchId
    );

    const totalInventoryAvailable = branchInventory.reduce((sum, inv) => {
      const available = inv.available_quantity !== undefined ? inv.available_quantity : inv.quantity;
      return sum + parseInt(available || 0, 10);
    }, 0);

    // Get original quantity from layaway
    const originalLayawayItem = layaway?.items?.find(li => li.item_id === itemId || li.id === itemId);
    const originalQuantity = originalLayawayItem ? originalLayawayItem.quantity : 0;

    // Calculate maximum we can have: inventory + what was originally reserved
    const maxAllowed = totalInventoryAvailable + originalQuantity;

    if (quantity > maxAllowed) {
      toast.error(`Cannot add more items. Maximum available: ${maxAllowed}`);
      return;
    }

    setCart(cart.map(cartItem =>
      cartItem.id === itemId
        ? { ...cartItem, quantity }
        : cartItem
    ));
  };

  const updateCartItemPrice = (itemId: string, newPrice: number) => {
    setCart(cart.map(cartItem =>
      cartItem.id === itemId
        ? { ...cartItem, price: newPrice }
        : cartItem
    ));
  };

  const removeFromCart = (itemId: string) => {
    setCart(cart.filter(item => item.id !== itemId));
  };

  const handleSave = async () => {
    if (!customer.name.trim()) {
      toast.error('Customer name is required');
      return;
    }

    if (cart.length === 0) {
      toast.error('Please add items to the debtor');
      return;
    }

    setIsLoading(true);
    try {
      const updateData = {
        customer_name: customer.name.trim(),
        customer_phone: customer.phone?.trim() || null,
        customer_address: customer.address?.trim() || null,
        total_amount: parseFloat(cartTotal.toFixed(2)),
        subtotal: parseFloat(cartSubtotal.toFixed(2)),
        product_handling: productHandling,
        payment_schedule_months: parseInt(paymentScheduleMonths.toString()),
        notes: notes?.trim() || null,
        layaway_date: layawayDate ? `${layawayDate} 00:00:00` : layawayDate,
        items: cart.map(item => ({
          item_id: item.id.toString(),
          quantity: parseInt(item.quantity.toString()),
          price: parseFloat(item.price.toString()),
        })),
      };

      await updateLayaway(layaway.id, updateData);
      toast.success('Debtor updated successfully');
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update debtor:', error);
      toast.error('Failed to update debtor');
    } finally {
      setIsLoading(false);
    }
  };

  return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" aria-describedby="edit-layaway-description">
          <DialogHeader>
          <DialogTitle>Edit Debtor #{layaway?.layaway_number}</DialogTitle>
          <p id="edit-layaway-description" className="text-sm text-gray-600 sr-only">
            Modify debtor details, customer information, items, and payment terms for debtor #{layaway?.layaway_number}.
          </p>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Side - Items Selection */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Items
                </CardTitle>
                <div className="relative mt-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search items..."
                    value={itemSearchTerm}
                    onChange={(e) => setItemSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardHeader>

              <CardContent className="max-h-[400px] overflow-y-auto">
                <div className="space-y-2">
                  {filteredItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex-1">
                        <div className="font-medium">{item.name}</div>
                        <div className="text-sm text-gray-500">
                          SKU: {item.sku} | {currencySymbol}{parseFloat(item.selling_price.toString()).toFixed(2)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={item.availableQuantity > 0 ? 'default' : 'destructive'}>
                          {item.availableQuantity} available
                        </Badge>
                        <Button
                          size="sm"
                          onClick={() => addToCart(item)}
                          disabled={item.availableQuantity <= 0}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Side - Order Details */}
          <div className="space-y-6">
            {/* Current Order */}
            <Card>
              <CardHeader>
                <CardTitle>Current Order</CardTitle>
              </CardHeader>
              <CardContent>
                {cart.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No items in order
                  </div>
                ) : (
                  <div className="space-y-2">
                    {cart.map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-2 border rounded">
                        <div className="flex-1">
                          <div className="font-medium text-sm">{item.name}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <Input
                              type="number"
                              step="0.01"
                              value={Number(item.price) === 0 ? '' : item.price.toFixed(2)}
                              onChange={(e) => {
                                const value = e.target.value;
                                updateCartItemPrice(item.id, value === '' ? 0 : (parseFloat(value) || 0));
                              }}
                              className="w-20 h-6 text-xs"
                            />
                            <span className="text-xs text-gray-500">each</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                            className="h-6 w-6 p-0"
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center text-sm">{item.quantity}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                            className="h-6 w-6 p-0"
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeFromCart(item.id)}
                            className="h-6 w-6 p-0 text-red-500"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}

                    {/* Totals */}
                    <div className="border-t pt-3 mt-3 space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>Subtotal:</span>
                        <span>{currencySymbol}{cartSubtotal.toFixed(2)}</span>
                      </div>
                      {cartVAT > 0 && (
                        <div className="flex justify-between text-sm">
                          <span>VAT ({vatPercentage}%):</span>
                          <span>{currencySymbol}{cartVAT.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold">
                        <span>Total:</span>
                        <span>{currencySymbol}{cartTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Paid Amount:</span>
                        <span>{currencySymbol}{(layaway?.paid_amount || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-orange-600">
                        <span>Remaining:</span>
                        <span>{currencySymbol}{remainingAfterPayments.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Customer Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Customer Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={customer.name}
                    onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                    placeholder="Customer name"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={customer.phone}
                    onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                    placeholder="Phone number"
                  />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input
                    value={customer.address}
                    onChange={(e) => setCustomer({ ...customer, address: e.target.value })}
                    placeholder="Address"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Payment Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Debtor Date</Label>
                  <Input
                    type="date"
                    value={layawayDate}
                    onChange={(e) => setLayawayDate(e.target.value)}
                    className="w-full"
                  />
                </div>

                <div>
                  <Label>Product Handling</Label>
                  <RadioGroup value={productHandling} onValueChange={(value: any) => setProductHandling(value)}>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="hold" id="hold" />
                      <Label htmlFor="hold">Hold Product (Reserve in store)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="handover" id="handover" />
                      <Label htmlFor="handover">Handover Product (Customer takes now)</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div>
                  <Label>Payment Schedule (Months)</Label>
                  <Select value={paymentScheduleMonths.toString()} onValueChange={(v) => setPaymentScheduleMonths(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                        <SelectItem key={m} value={m.toString()}>{m} month{m > 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Notes</Label>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional notes..."
                  />
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isLoading}>
                {isLoading ? (
                  'Saving...'
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
